import React from 'react';
import { Wind, Flame, Mountain } from 'lucide-react';

type DoshaType = 'vata' | 'pitta' | 'kapha' | 'vata-pitta' | 'pitta-kapha' | 'vata-kapha' | 'sama';

interface DoshaIllustrationProps {
  dosha: DoshaType;
  className?: string;
}

/**
 * DoshaIllustration using actual Lucide-react icons
 * Standardizes the iconography to match the rest of the application.
 * Uses translucent white strokes to work on dark/vibrant backgrounds.
 */

const DoshaIllustration: React.FC<DoshaIllustrationProps> = ({ dosha, className = "w-[72px] h-[72px]" }) => {
  const getDoshaList = () => {
    if (dosha.toLowerCase() === 'sama') return ['vata', 'pitta', 'kapha'];
    return dosha.toLowerCase().split('-');
  };

  const doshaList = getDoshaList();

  const renderIcon = (name: string, size: number) => {
    const props = { 
      size: size, 
      strokeWidth: 1.5, 
      color: "rgba(255,255,255,0.8)",
      className: "drop-shadow-sm" 
    };

    switch (name.toLowerCase()) {
      case 'vata': return <Wind {...props} />;
      case 'pitta': return <Flame {...props} />;
      case 'kapha': return <Mountain {...props} />;
      default: return null;
    }
  };

  return (
    <div className={`rounded-2xl bg-white/10 p-3 backdrop-blur-md flex items-center justify-center overflow-hidden shrink-0 border border-white/10 shadow-inner ${className}`}>
      <div className="flex w-full h-full gap-2 items-center justify-center">
        {doshaList.map((d, index) => (
          <div 
            key={index} 
            className="flex items-center justify-center transition-all duration-500 hover:scale-110"
            style={{ 
              width: doshaList.length === 3 ? '30%' : doshaList.length === 2 ? '45%' : '100%',
              opacity: 1 - (index * 0.15) // Subtle depth for dual/tri dosha
            }}
          >
            {renderIcon(d, doshaList.length === 3 ? 24 : doshaList.length === 2 ? 32 : 44)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DoshaIllustration;
